import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import GardenModal from './GardenModal';
import { plants, photos, apiFetch } from '../services/api';
import './PlantDetail.css';

const API_URL = process.env.REACT_APP_API_URL

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function PlantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [plant, setPlant] = useState(null);
  const [plantPhotos, setPlantPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();
  const [uploading, setUploading] = useState(false);
  const [aiNotes, setAiNotes] = useState([]);
  const [userNotes, setUserNotes] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [modal, setModal] = useState({ isOpen: false });

  useEffect(() => {
    loadPlantData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadPlantData = async () => {
    try {
      setLoading(true);
      const [plantRes, photosRes] = await Promise.all([
        plants.getOne(id),
        photos.getForPlant(id)
      ]);
      setPlant(plantRes.data.plant);
      setPlantPhotos(photosRes.data.photos);
      setUserNotes(plantRes.data.plant.notes || '');
      try {
        const notesResponse = await apiFetch(`${API_URL}/plants/${id}/ai-notes`);
        if (notesResponse.ok) {
          const notesData = await notesResponse.json();
          setAiNotes(notesData.notes || []);
        }
      } catch (err) {
        console.log('No AI notes found');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading plant:', error);
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('photo', file);
      await photos.uploadForPlant(id, formData);
      const photosRes = await photos.getForPlant(id);
      setPlantPhotos(photosRes.data.photos);
      setUploading(false);
    } catch (error) {
      console.error('Photo upload error:', error);
      setUploading(false);
      if (error.response?.data?.error === 'PHOTO_LIMIT_REACHED') {
        setModal({
          isOpen: true, type: 'alert', title: 'Photo Limit Reached',
          message: 'You\'ve reached the free plan limit of 3 photos per plant. Unlimited photos are coming soon with Yardray Pro — stay tuned!',
          onConfirm: () => setModal({ isOpen: false })
        });
      } else {
        setModal({
          isOpen: true, type: 'alert', title: 'Error',
          message: 'Failed to upload photo',
          onConfirm: () => setModal({ isOpen: false })
        });
      }
    }
  };

  const saveNotes = async () => {
    try {
      await apiFetch(`${API_URL}/plants/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          commonName: plant.common_name,
          scientificName: plant.scientific_name,
          plantType: plant.plant_type,
          datePlanted: plant.date_planted,
          notes: userNotes
        })
      });
      setEditingNotes(false);
      loadPlantData();
    } catch (error) {
      console.error('Failed to save notes:', error);
      setModal({
        isOpen: true, type: 'alert', title: 'Error',
        message: 'Failed to save notes',
        onConfirm: () => setModal({ isOpen: false })
      });
    }
  };

  const getGenericPlantImage = (plantName) => {
    if (!plantName) return null;
    return `https://source.unsplash.com/400x300/?${encodeURIComponent(plantName)},plant,vegetable`;
  };

  if (loading) {
    return <div className="plant-detail-loading"><p>Loading plant details...</p></div>;
  }

  if (!plant) {
    return (
      <div className="plant-detail-not-found">
        <p>Plant not found</p>
        <button onClick={() => navigate('/plants')}>Back to Plants</button>
      </div>
    );
  }

  const displayImage = plantPhotos.length > 0
    ? plantPhotos[0].thumbnail_urls?.medium || plantPhotos[0].original_url
    : getGenericPlantImage(plant.common_name);

  return (
    <div className={`plant-detail-container${isMobile ? ' plant-detail-container--mobile' : ''}`}>
      <GardenModal isOpen={modal.isOpen} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} onCancel={() => setModal({ isOpen: false })} />

      <div className="plant-detail-header">
        <h2>{plant.common_name}</h2>
        <div className="plant-detail-btn-group">
          <button className="plant-detail-btn-edit" onClick={() => navigate(`/plants/${id}/edit`)}>Edit</button>
          <button className="plant-detail-btn-back" onClick={() => navigate('/plants')}>Back</button>
        </div>
      </div>

      <div className={`plant-detail-grid${isMobile ? ' plant-detail-grid--mobile' : ''}`}>
        <div>
          <div className="plant-detail-photo-wrap">
            {displayImage ? (
              <img
                src={displayImage}
                alt={plant.common_name}
                className="plant-detail-photo"
                onError={(e) => { e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23ddd" width="400" height="300"/%3E%3C/svg%3E'; }}
              />
            ) : (
              <div className="plant-detail-photo-placeholder">🌱</div>
            )}
          </div>

          <div className="plant-detail-upload-wrap">
            {uploading ? (
              <div className="plant-detail-uploading">Uploading...</div>
            ) : (
              <div className="plant-detail-photo-btns">
                <label className="plant-detail-btn-camera">
                  📷 Take Photo
                  <input type="file" accept="image/*" capture="environment"
                    onChange={handlePhotoUpload} disabled={uploading} style={{ display: 'none' }} />
                </label>
                <label className="plant-detail-btn-gallery">
                  🖼️ Choose Photo
                  <input type="file" accept="image/*"
                    onChange={handlePhotoUpload} disabled={uploading} style={{ display: 'none' }} />
                </label>
              </div>
            )}
          </div>

          {plantPhotos.length > 0 && (
            <div>
              <h3>Photos ({plantPhotos.length})</h3>
              <div className="plant-detail-photos-grid">
                {plantPhotos.map(photo => (
                  <div key={photo.id} className="plant-detail-thumb-wrap">
                    <img
                      src={photo.thumbnail_urls?.small || photo.original_url}
                      alt="Plant"
                      className="plant-detail-thumb"
                      onClick={() => window.open(photo.original_url, '_blank')}
                    />
                    <button
                      className="plant-detail-thumb-delete"
                      onClick={async (e) => {
                        e.stopPropagation();
                        setModal({
                          isOpen: true, type: 'confirm', title: 'Delete Photo',
                          message: 'Delete this photo?',
                          onConfirm: async () => {
                            setModal({ isOpen: false });
                            try {
                              await apiFetch(`${API_URL}/photos/${photo.id}`, { method: 'DELETE' });
                              const photosRes = await photos.getForPlant(id);
                              setPlantPhotos(photosRes.data.photos);
                            } catch (err) { console.error('Delete photo error:', err); }
                          },
                          onCancel: () => setModal({ isOpen: false })
                        });
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="plant-detail-info-card">
            <h3>Plant Information</h3>
            {plant.scientific_name && <p><strong>Scientific Name:</strong> <em>{plant.scientific_name}</em></p>}
            {plant.plant_type && <p><strong>Type:</strong> {plant.plant_type}</p>}
            {plant.date_planted && <p><strong>Date Planted:</strong> {new Date(plant.date_planted).toLocaleDateString()}</p>}
            {plant.property_name && <p><strong>Property:</strong> {plant.property_name}</p>}
            <p><strong>Added:</strong> {new Date(plant.created_at).toLocaleDateString()}</p>
          </div>

          <div className="plant-detail-notes-card">
            <div className="plant-detail-notes-header">
              <h3>My Notes</h3>
              {!editingNotes ? (
                <button className="plant-detail-btn-edit-notes" onClick={() => setEditingNotes(true)}>Edit</button>
              ) : (
                <div className="plant-detail-notes-edit-btns">
                  <button className="plant-detail-btn-save" onClick={saveNotes}>Save</button>
                  <button className="plant-detail-btn-cancel" onClick={() => { setEditingNotes(false); setUserNotes(plant.notes || ''); }}>Cancel</button>
                </div>
              )}
            </div>
            {editingNotes ? (
              <textarea
                className="plant-detail-notes-textarea"
                value={userNotes}
                onChange={(e) => setUserNotes(e.target.value)}
                placeholder="Add notes about watering schedule, fertilizer used, observations, etc."
                rows="6"
              />
            ) : (
              <p className={`plant-detail-notes-text${!userNotes ? ' plant-detail-notes-text--empty' : ''}`}>
                {userNotes || 'No notes yet. Click Edit to add notes about care, observations, products used, etc.'}
              </p>
            )}
          </div>

          {plant.care_pruning_months && plant.care_pruning_months.includes(new Date().getMonth() + 1) && (
            <div className="plant-detail-prune-alert">
              <span className="plant-detail-prune-icon">✂️</span>
              <div>
                <p className="plant-detail-prune-title">Time to prune</p>
                <p className="plant-detail-prune-text">{plant.common_name} is typically pruned this month.</p>
              </div>
            </div>
          )}

          {(plant.care_water_frequency || plant.care_sun_requirement || plant.bloom_months || plant.care_pruning_months || plant.mulch_months || plant.transplant_months || plant.harvest_months) && (
            <div className="plant-detail-care-card">
              <h3>Care Guide</h3>
              <div className="plant-detail-care-tags">
                {plant.care_water_frequency && (
                  <span className="care-tag care-tag--water">Watering: every {plant.care_water_frequency} days</span>
                )}
                {plant.care_sun_requirement && (
                  <span className="care-tag care-tag--sun">Sun: {plant.care_sun_requirement}</span>
                )}
                {plant.frost_tolerant !== null && plant.frost_tolerant !== undefined && (
                  <span className={`care-tag ${plant.frost_tolerant ? 'care-tag--frost-tolerant' : 'care-tag--frost-sensitive'}`}>
                    {plant.frost_tolerant ? 'Frost tolerant' : 'Not frost tolerant'}
                  </span>
                )}
                {plant.bloom_months?.length > 0 && (
                  <span className="care-tag care-tag--bloom">
                    Blooms: {plant.bloom_months.map(m => MONTH_NAMES[m-1]).join(', ')}
                  </span>
                )}
                {plant.care_pruning_months?.length > 0 && (
                  <span className="care-tag care-tag--prune">
                    Prune: {plant.care_pruning_months.map(m => MONTH_NAMES[m-1]).join(', ')}
                  </span>
                )}
                {plant.mulch_months?.length > 0 && (
                  <span className="care-tag care-tag--mulch">
                    Mulch: {plant.mulch_months.map(m => MONTH_NAMES[m-1]).join(', ')}
                  </span>
                )}
                {plant.transplant_months?.length > 0 && (
                  <span className="care-tag care-tag--transplant">
                    Transplant: {plant.transplant_months.map(m => MONTH_NAMES[m-1]).join(', ')}
                  </span>
                )}
                {plant.harvest_months?.length > 0 && (
                  <span className="care-tag care-tag--harvest">
                    Harvest: {plant.harvest_months.map(m => MONTH_NAMES[m-1]).join(', ')}
                  </span>
                )}
              </div>
            </div>
          )}

          {(plant.poisonous_to_humans || plant.poisonous_to_pets) && (
            <div className="plant-detail-toxicity-card">
              <h3>Toxicity Warning</h3>
              <div className="plant-detail-toxicity-tags">
                {plant.poisonous_to_humans && (
                  <span className="plant-detail-toxicity-tag">Toxic to humans</span>
                )}
                {plant.poisonous_to_pets && (
                  <span className="plant-detail-toxicity-tag">Toxic to pets</span>
                )}
              </div>
            </div>
          )}

          {aiNotes.length > 0 && (
            <div className="plant-detail-ai-card">
              <h3>AI Insights</h3>
              {aiNotes.map(note => (
                <div key={note.id} className="plant-detail-ai-note">
                  <p className="plant-detail-ai-type">{note.note_type}</p>
                  <p className="plant-detail-ai-content">{note.content}</p>
                  <p className="plant-detail-ai-meta">
                    Confidence: {Math.round(note.confidence * 100)}% • {new Date(note.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PlantDetail;
